import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Plus, Edit2, Trash2, Calendar as CalendarIcon, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventNotes = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', event_type: '', event_date: '' });

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customer/event-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(response.data.notes || []);
    } catch (error) {
      console.error('Error loading notes:', error);
      toast.error('Fehler beim Laden der Notizen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Titel und Inhalt sind erforderlich');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (editingNote?.id) {
        await axios.put(`${API}/customer/event-notes/${editingNote.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Notiz aktualisiert');
      } else {
        await axios.post(`${API}/customer/event-notes`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Notiz erstellt');
      }
      setFormData({ title: '', content: '', event_type: '', event_date: '' });
      setEditingNote(null);
      loadNotes();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Notiz wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/customer/event-notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notiz gelöscht');
      loadNotes();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) return <div className="bg-white rounded-3xl shadow-xl p-4 md:p-8 flex items-center justify-center py-12"><Loader className="w-8 h-8 animate-spin text-[#2B2142]" /></div>;

  return (
    <div className="bg-white rounded-3xl shadow-xl p-4 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 md:w-6 md:h-6 text-[#2B2142] flex-shrink-0" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Event-Notizen</h2>
        </div>
        <button 
          onClick={() => setEditingNote({})} 
          className="w-full sm:w-auto px-4 py-2.5 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl flex items-center justify-center gap-2 touch-manipulation min-h-[44px]"
        >
          <Plus className="w-5 h-5" /> Neue Notiz
        </button>
      </div>

      {editingNote && (
        <div className="mb-6 p-4 md:p-6 border-2 border-[#2B2142]/20 rounded-2xl bg-[#FFF0D1]/30">
          <h3 className="font-bold text-base md:text-lg mb-4">{editingNote.id ? 'Notiz bearbeiten' : 'Neue Notiz'}</h3>
          
          {/* Title */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-semibold text-gray-700">Titel</label>
              {formData.title.length > 80 && (
                <span className={`text-xs ${formData.title.length >= 100 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                  {formData.title.length}/100
                </span>
              )}
            </div>
            <input
              type="text"
              placeholder="z.B. Hochzeit Max & Anna"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              maxLength={100}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none text-base touch-manipulation min-h-[44px]"
            />
          </div>

          {/* Content */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-semibold text-gray-700">Inhalt</label>
              {formData.content.length > 1600 && (
                <span className={`text-xs ${formData.content.length >= 2000 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                  {formData.content.length}/2000
                </span>
              )}
            </div>
            <textarea
              placeholder="Notizen zum Event..."
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              rows={4}
              maxLength={2000}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none text-base resize-none touch-manipulation"
            />
          </div>

          {/* Event Type and Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-semibold text-gray-700">Event-Typ</label>
                {formData.event_type.length > 80 && (
                  <span className={`text-xs ${formData.event_type.length >= 100 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                    {formData.event_type.length}/100
                  </span>
                )}
              </div>
              <input
                type="text"
                placeholder="z.B. Hochzeit, Geburtstag"
                value={formData.event_type}
                onChange={(e) => setFormData({...formData, event_type: e.target.value})}
                maxLength={100}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none text-base touch-manipulation min-h-[44px] bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Datum</label>
              <div className="relative w-full">
                <input 
                  type="date" 
                  value={formData.event_date} 
                  onChange={(e) => setFormData({...formData, event_date: e.target.value})} 
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none text-base touch-manipulation min-h-[44px] bg-white appearance-none cursor-pointer"
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    colorScheme: 'light'
                  }}
                />
                <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-10" />
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={handleSave} 
              className="w-full sm:w-auto px-6 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white rounded-xl font-semibold transition-colors touch-manipulation min-h-[44px]"
            >
              Speichern
            </button>
            <button 
              onClick={() => { 
                setEditingNote(null); 
                setFormData({ title: '', content: '', event_type: '', event_date: '' }); 
              }} 
              className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 hover:border-gray-400 text-gray-700 rounded-xl font-semibold transition-colors touch-manipulation min-h-[44px]"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 md:w-16 md:h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-sm md:text-base text-gray-600">Noch keine Notizen vorhanden.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {notes.map((note) => (
            <div key={note.id} className="border border-gray-200 rounded-2xl p-4 md:p-6 overflow-hidden">
              <div className="flex justify-between items-start gap-3 mb-3">
                <h3 className="font-bold text-base md:text-lg flex-1 min-w-0 break-all">{note.title}</h3>
                <div className="flex gap-2 flex-shrink-0">
                  <button 
                    onClick={() => { 
                      setEditingNote(note); 
                      setFormData({ 
                        title: note.title, 
                        content: note.content, 
                        event_type: note.event_type || '', 
                        event_date: note.event_date || '' 
                      }); 
                    }} 
                    className="p-2.5 text-[#2B2142] hover:bg-[#FFF0D1]/30 rounded-lg transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleDelete(note.id)} 
                    className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm md:text-base text-gray-700 mb-3 whitespace-pre-wrap break-all">{note.content}</p>
              {(note.event_type || note.event_date) && (
                <div className="text-xs md:text-sm text-gray-500 flex flex-wrap gap-4">
                  {note.event_type && <span className="flex items-center gap-1 break-all"><FileText className="w-3 h-3 flex-shrink-0" /> {note.event_type}</span>}
                  {note.event_date && <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> {new Date(note.event_date).toLocaleDateString('de-DE')}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventNotes;
